import Input from "../Atoms/Input";
import React, {ChangeEvent, useEffect} from "react";
import Button from "../Atoms/Button";
import {useAppDispatch} from "../redux/hooks";
import {setActiveContent, setIsAppLoading} from "../redux/slices/content/contentSlice";
import {ActiveContent} from "../redux/slices/content/contentType";
import {useMutation} from "@apollo/client";
import {SignUp} from "../GraphQL/LoginMutations";
import {ErrorMessage} from "../Helper/ErrorMessage";
import {setUser} from "../redux/slices/user/userSlice";
import {toast} from "react-toastify";

export const Registration = () => {
    const dispatch = useAppDispatch();

    const [signUp, { data: signUpData, error: signUpError, loading: signUpLoading }] = useMutation(SignUp);

    const [userName, setUserName] = React.useState<string>("");
    const [userNameError, setUserNameError] = React.useState<string>("");
    const [password, setPassword] = React.useState<string>("");
    const [confirmPassword, setConfirmPassword] = React.useState<string>("");
    const [passwordError, setPasswordError] = React.useState<string>("");
    const [urgencyOfPasswordError, setUrgencyOfPasswordError] = React.useState<"critical" | "moderate" | "low" | "">("")
    const [confirmPasswordError, setConfirmPasswordError] = React.useState<string>("");
    const [mail, setMail] = React.useState<string>("");
    const [mailError, setMailError] = React.useState<string>("");

    useEffect(() => {
        if (signUpData && !signUpLoading) {
            localStorage.setItem('token', signUpData.signup.token)
            dispatch(setUser({
                userEmail: signUpData.signup.user.userEmail,
                role: signUpData.signup.user.userRole,
                userName: signUpData.signup.user.userName,
                userId: signUpData.signup.user.userId,
                lastHostedAdventure: signUpData.signup.user.lastHostedAdventure,
                lastPlayedAdventure: signUpData.signup.user.lastPlayedAdventure,
            }))
            dispatch(setActiveContent(ActiveContent.Dashboard))
        }
    }, [ signUpData ])

    useEffect(() => {
        if (signUpError) {
            toast('Es ist ein Fehler bei der Registrierung aufgetreten')
        }
    }, [ signUpError ])

    useEffect(() => {
        if (signUpLoading) {
            dispatch(setIsAppLoading({
                isAppLoading: true,
                loadingText: "Sie werden registriert..."
            }))
        } else {
            dispatch(setIsAppLoading({
                isAppLoading: false,
                loadingText: ""
            }))
        }
    }, [ signUpLoading ])

    const validateAndSetUserName = (value: string) => {
        setUserName(value);
        if (!/^(?=[a-zA-Z0-9._]{4,20}$)(?!.*[_.]{2})[^_.].*[^_.]$/.test(value)) {
            setUserNameError("Der Nutzername ist nicht gültig!")
        } else {
            setUserNameError("")
        }
    }

    const validateAndSetConfirmPassword = ( value: string) => {
        setConfirmPassword(value);
        if (value !== password) {
            setConfirmPasswordError("Die Passwörter stimmen nicht überein!")
        } else {
            setConfirmPasswordError("");
        }
    }

    const validateAndSetPassword = (value: string) => {
        setPassword(value);
        if (value.length < 6) {
            setPasswordError("Dein Password muss länger als 6 Zeichen sein");
            setUrgencyOfPasswordError("critical")
            return;
        }
        if (!/^(?=.*\d).{0,16}$/.test(value)) {
            setPasswordError("Dein Passwort muss mindestens eine Zahl beinhalten")
            setUrgencyOfPasswordError("critical")
            return;
        }
        if (!/^(?=.*[a-zA-Z]).{0,16}$/.test(value)) {
            setPasswordError("Dein Passwort muss mindestens ein Buchstaben enthalten");
            setUrgencyOfPasswordError("critical")
            return;
        }
        if (!/^(?=.*[A-Z]).{0,16}$/.test(value)) {
            setPasswordError("Dein Passwort muss mindestens einen Großbuchstaben enthalten");
            setUrgencyOfPasswordError("critical")
            return;
        }
        if (!/^(?=.*[-#$.%!&*]).{0,16}$/.test(value)) {
            setPasswordError("Dein Passwort muss mindestens ein Sonderzeichen enthalten");
            setUrgencyOfPasswordError("critical")
            return;
        }
        if (value.length < 8) {
            setPasswordError("Dein Passwort sollte mindestens 8 Zeichen enthalten");
            setUrgencyOfPasswordError("moderate")
            return;
        }
        setPasswordError("")
        setUrgencyOfPasswordError("")
    }

    const validateAndSetMail = (value: string) => {
        setMail(value)
        if (!(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value))) {
            setMailError("Bitte geben sie eine gültige Email ein!")
        } else {
            setMailError("")
        }
    }

    const handleSignUp = async() => {
        if (isSignUpAvailable()) {
            await signUp({
                variables: {
                    userEmail: mail,
                    userPassword: password,
                    userName: userName
                }
            })
        }
    }

    const isSignUpAvailable = () => {
        if (confirmPasswordError !== "") {
            return false;
        }
        if (confirmPassword === "") {
            return false;
        }
        if (password === "") {
            return false;
        }
        if (urgencyOfPasswordError === "critical") {
            return false;
        }
        if (mail === "") {
            return false;
        }
        if (mailError !== "") {
            return false;
        }
        if (userName === "") {
            return false;
        }
        return userNameError === "";


    }

    return (
        <div className='row login-container'>
            <div className='col-5 login-left-container'>
                <div className='login-left-container-logo'>
                    <div className='col-5'>VENE DND</div>
                </div>
            </div>
            <div className='col-7 login-right-container'>
                <div className='row justify-content-center align-content-center' style={{ height: 'calc(100vh - 20px)' }}>
                    <div className='col-10 login-right-inner-container'>
                        <div className='row'>
                            <h3>Registrierung</h3>
                        </div>
                        <div className='row justify-content-center'>
                            <div className='col-10'>
                                <Input
                                    label='Benutzername:'
                                    onChange={ (event: ChangeEvent<HTMLInputElement>) => validateAndSetUserName(event.target.value) }
                                    onKeyDown={ async (event: any) => {
                                        if (event.key === 'Enter') {
                                            await handleSignUp()
                                        }
                                    }}
                                    value={userName}
                                />
                            </div>
                        </div>
                        { userNameError !== "" && (
                            <div className='row justify-content-start'>
                                <div className='col-10'>
                                    <ErrorMessage text={ userNameError } urgency={ "critical" }/>
                                </div>
                            </div>
                        ) }
                        <div className='row justify-content-center'>
                            <div className='col-10'>
                                <Input
                                    label='E-Mail Adresse:'
                                    onChange={ (event: ChangeEvent<HTMLInputElement>) => validateAndSetMail(event.target.value)}
                                    value={mail}
                                    onKeyDown={ async (event: any) => {
                                        if (event.key === 'Enter') {
                                            await handleSignUp()
                                        }
                                    }}
                                    type='email'
                                />
                            </div>
                        </div>
                        { mailError !== "" && (
                            <div className='row justify-content-start'>
                                <div className='col-10'>
                                    <ErrorMessage text={ mailError } urgency={ "critical" }/>
                                </div>
                            </div>
                        ) }
                        <div className='row justify-content-center'>
                            <div className='col-10'>
                                <Input
                                    label='Passwort:'
                                    onChange={ (event: ChangeEvent<HTMLInputElement>) => validateAndSetPassword(event.target.value)}
                                    value={password}
                                    onKeyDown={ async (event: any) => {
                                        if (event.key === 'Enter') {
                                            await handleSignUp()
                                        }
                                    }}
                                    type='password'
                                />
                            </div>
                        </div>
                        { passwordError !== "" && (
                            <div className='row justify-content-start'>
                                <div className='col-10'>
                                    <ErrorMessage text={ passwordError } urgency={ urgencyOfPasswordError }/>
                                </div>
                            </div>
                        ) }
                        <div className='row justify-content-center'>
                            <div className='col-10'>
                                <Input
                                    label='Passwort bestätigen:'
                                    onChange={ (event: ChangeEvent<HTMLInputElement>) => validateAndSetConfirmPassword(event.target.value)}
                                    value={confirmPassword}
                                    onKeyDown={ async (event: any) => {
                                        if (event.key === 'Enter') {
                                            await handleSignUp()
                                        }
                                    }}
                                    type='password'
                                />
                            </div>
                        </div>
                        { confirmPasswordError !== "" && (
                            <div className='row justify-content-start'>
                                <div className='col-10'>
                                    <ErrorMessage text={ confirmPasswordError } urgency={ "critical" }/>
                                </div>
                            </div>
                        ) }
                        <div className='row justify-content-center mt-3'>
                            <div className='col-6'>
                                <Button
                                    className='login-btn'
                                    onClick={ async () => await handleSignUp() }
                                    disabled={ !isSignUpAvailable() }
                                >
                                    <span>Registrieren</span>
                                </Button>
                            </div>
                        </div>
                        <div className='row justify-content-center mt-2'>
                            <div className='col-6'>
                                <Button
                                    onClick={ async () => dispatch(setActiveContent(ActiveContent.Dashboard)) }
                                >
                                    <span>Zur Anmeldung</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}